import connectionTypes from '../../consts/connectionTypes';
import * as damConnectionStatusTypes from '../../consts/dam/damConnectionStatusTypes';
import settingNames from '../../consts/device/settings/settingNames';
import * as uamfCommunicationInterfaceTypes from '../../consts/uamf/uamfCommunicationInterfaceTypes';
import * as uamfConnectionStatusTypes from '../../consts/uamf/uamfConnectionStatusTypes';
import * as uamfWebSocketEventTypes from '../../consts/uamf/uamfWebSocketEventTypes';
import objectUtil from '../../utils/objectUtil';
import deviceHelpers from '../device/deviceHelpers';

const updateSettingMapping = (response) => ({requestId: response?.data?.requestId});

const getDeviceSettingFields = (data) => {
    const settings = objectUtil.filterUndefined({
        [settingNames.VIBRATION_INTENSITY]: data?.vibrationIntensity?.percentage,
        [settingNames.AUTO_START]: data?.autoStartHeating?.featureState,
        [settingNames.TUNABILITY]: data?.tunability?.featureState,
        [settingNames.REMEMBER_HEATING_PROFILE]: data?.rememberHeatingProfile?.featureState,
        [settingNames.RANGE_MODE]: data?.rangeMode?.featureState,
    });

    return Object.keys(settings).length > 0 ? {settings} : {};
};

const deviceInfoMapping = (response) => {
    const asset = response?.data?.[0];

    return {
        ...objectUtil.filterUndefined({
            assetId: asset?.assetId,
            serialNumber: asset?.device?.serialNumber,
            codentify: asset?.device?.codentify,
            firmwareVersion: asset?.device?.firmwareVersion,
            batteryCharge: deviceHelpers.deviceBatteryChargeValueValidation(+asset?.device?.batteryInfo?.batteryCharge),
        }),
        ...getDeviceSettingFields(asset?.device),
    };
};

const getDeviceFields = (data) =>
    objectUtil.filterUndefined({
        batteryCharge: deviceHelpers.deviceBatteryChargeValueValidation(+data?.batteryInfo?.batteryCharge),
        codentify: data?.codentify,
        firmwareVersion: data?.firmwareVersion,
    });

const subscriptionMapping = (response) => {
    const data = response?.data;

    return {
        url: data?.url,
        expiresAt: data?.expiresAt,
        assetId: data?.assetId,
    };
};

const communicationInterfaceMapping = (communicationInterface) => {
    switch (communicationInterface) {
        case uamfCommunicationInterfaceTypes.WIFI:
            return connectionTypes.WIFI;
        case uamfCommunicationInterfaceTypes.CELLULAR:
            return connectionTypes.CELLULAR;
        default:
            return connectionTypes.WIFI;
    }
};

const normalizeConnectionStatus = (status) =>
    status === uamfConnectionStatusTypes.CONNECTED
        ? damConnectionStatusTypes.CONNECTED
        : damConnectionStatusTypes.DISCONNECTED;

const connectionStatusFieldsMapping = (data) => ({
    status: normalizeConnectionStatus(data?.status),
    connectionType: communicationInterfaceMapping(data?.communicationInterface),
    lastChargerDusn: data?.connectedThrough, // Only for holders
    dependantDevice: data?.dependantDevice, // Only for chargers
    dependantDeviceCodentify: data?.dependantDeviceCodentify, // Only for chargers
});

const deviceConnectionStatusMapping = (response) => connectionStatusFieldsMapping(response?.data);

const deviceSynchronisationMapping = (response) => {
    return response?.data?.requestId;
};

const registrationMapping = (response) => {
    const data = response?.data;

    return {
        device: {
            dusn: data?.serialNumber,
            assetId: data?.assetId,
            codentify: data?.codentify,
        },
    };
};

const requestStatusMapping = (response) => {
    const data = response?.data;

    return {
        eventType: data?.eventType || uamfWebSocketEventTypes.ASYNC_REQUEST,
        payload: {
            requestId: data?.requestId,
            status: data?.status,
            serialNumber: data?.serialNumber,
            request: data?.responsePayload?.request,
            errorMessage: data?.errorMessage,
            responsePayload: data?.responsePayload,
        },
    };
};

const insightsAndTipsMapping = (tipsAndTricks) =>
    Object.fromEntries(
        Object.entries(tipsAndTricks || {}).map(([insightId, insight]) => {
            const {require_engagement, ...insightData} = insight || {};

            return [insightId, {...insightData, tt_require_engagement: require_engagement}];
        })
    );

const experienceInferenceMapping = (response) => {
    const responseData = response?.data?.[0];

    if (!responseData) return {};

    const {inferredOn, result} = responseData;
    const calibration = result?.Calibration;

    const {n_sticks_identified, min_n_experiences_required} = calibration || {};

    if (n_sticks_identified < min_n_experiences_required) {
        return {
            inferenceTimestamp: inferredOn,
            calibration,
            experienceScore: null,
            tipsToConsider: null,
            insightsAndTips: null,
            rawItems: response.data,
        };
    }

    const {tips_to_consider, ...experienceScoreData} = result?.experience_score || {};

    return {
        inferenceTimestamp: inferredOn,
        calibration,
        experienceScore: experienceScoreData,
        tipsToConsider: tips_to_consider,
        insightsAndTips: insightsAndTipsMapping(result?.TipsAndTricks),
        rawItems: response.data,
    };
};

export default {
    deviceInfoMapping,
    getDeviceFields,
    getDeviceSettingFields,
    updateSettingMapping,
    subscriptionMapping,
    deviceConnectionStatusMapping,
    connectionStatusFieldsMapping,
    deviceSynchronisationMapping,
    registrationMapping,
    requestStatusMapping,
    normalizeConnectionStatus,
    experienceInferenceMapping,
};
